import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import {
  Artifact,
  type AddArtifactRequest,
  type UpdateArtifactRequest,
  type AddArtifactResponse
} from '@bliks-io/contracts';
import { useArtifacts } from '@/api/queries/useArtifacts';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import {
  useCreateArtifact,
  useUpdateArtifact,
  useDeleteArtifact
} from '@/api/mutations/useArtifacts';
import {
  Badge,
  getColorSchemeFromString,
  createColumnHeader,
  renderFallback,
  createActionsColumn,
  createDateColumn,
  createUserCardColumn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { ArtifactFormDialog } from './components/ArtifactFormDialog';
import { useAuthStore } from '@/stores/auth-store';
import { OrganizationTabsNav } from '@/components/organization/OrganizationTabsNav';
import {
  createEntityListPage,
  type EntityListPageDescriptor
} from '@/components/entity-list/EntityListPage';

export const artifactsDescriptor: EntityListPageDescriptor<
  Artifact,
  AddArtifactRequest,
  UpdateArtifactRequest,
  AddArtifactResponse
> = {
  documentTitleKey: 'pageTitles.artifacts',
  i18nPrefix: 'organization.artifacts',
  icon: FileText,
  TabsNav: OrganizationTabsNav,
  tableId: 'artifacts',
  enableGlobalFilter: true,
  useRows: () => useArtifacts(),
  useMutations: () => ({
    create: useCreateArtifact(),
    update: useUpdateArtifact(),
    remove: useDeleteArtifact()
  }),
  useColumns: ({ openEdit, openDelete }) => {
    const { t } = useTranslation();
    const slug = useAuthStore().slug();
    const navigate = useNavigate();
    const userLookup = useUserTombstoneLookup();

    const handleView = useCallback(
      (artifact: Artifact) => {
        navigate({
          to: '/$slug/artifacts/$artifactId',
          params: { slug, artifactId: artifact._id }
        });
      },
      [navigate, slug]
    );

    return useMemo<DataTableColumnDef<Artifact>[]>(
      () => [
        {
          accessorKey: 'name',
          meta: { label: t('common.Name'), filterType: 'text' },
          header: createColumnHeader(t('common.Name')),
          cell: ({ row }) => (
            <Link
              to="/$slug/artifacts/$artifactId"
              params={{ slug, artifactId: row.original._id }}
              className="font-medium hover:underline"
            >
              {row.getValue('name')}
            </Link>
          ),
          enableColumnFilter: true
        },
        {
          accessorKey: 'description',
          meta: { label: t('common.Description'), filterType: 'text' },
          header: createColumnHeader(t('common.Description')),
          cell: ({ row }) => renderFallback(row.getValue('description')),
          enableColumnFilter: true
        },
        {
          accessorKey: 'type',
          meta: {
            label: t('common.Type'),
            filterType: 'select',
            filterOptions: [
              { label: t('organization.artifacts.types.document'), value: 'document' },
              { label: t('organization.artifacts.types.image'), value: 'image' },
              { label: t('organization.artifacts.types.video'), value: 'video' },
              { label: t('organization.artifacts.types.other'), value: 'other' }
            ]
          },
          header: createColumnHeader(t('common.Type')),
          cell: ({ row }) => {
            const type = row.getValue('type') as string | undefined;
            if (!type) return renderFallback(type);
            return (
              <Badge shape="rounded" colorScheme={getColorSchemeFromString(type)}>
                {t(`organization.artifacts.types.${type}`, { defaultValue: type })}
              </Badge>
            );
          },
          enableColumnFilter: true
        },
        createUserCardColumn<Artifact>('createdBy', t('organization.artifacts.createdBy'), {
          variant: 'with-role',
          itemVariant: 'outline',
          size: 'sm',
          enableFiltering: true,
          userLookup
        }),
        createDateColumn<Artifact>('createdAt', t('common.Created'), { enableFiltering: true }),
        createDateColumn<Artifact>('updatedAt', t('common.Updated'), { enableFiltering: true }),
        createActionsColumn<Artifact>({
          onEdit: openEdit,
          onDelete: (artifact) => openDelete(artifact._id),
          onView: handleView
        })
      ],
      [t, slug, handleView, openEdit, openDelete, userLookup]
    );
  },
  renderFormDialog: ({ open, onOpenChange, entity, onSubmit }) => (
    <ArtifactFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      artifact={entity}
    />
  ),
  deleteEntityPhraseKey: 'organization.artifacts.deletePhrase'
};

export const ArtifactsListPage = createEntityListPage(artifactsDescriptor);
